import _, { isNil } from 'lodash';
import moment from 'moment';
import SgDateUtils from 'sg-date-utils';
import SgSptConstants from 'sg-spt-constants';
import { SgDateUtil, SgSessionStorageService } from 'saga-modules';

const {
    SINGLE_TRIP_CANCELLATION_LIMITS,
    COVER_LEVELS,
    EXCESS_CODES,
    MAX_ESSENTIAL_SINGLE_TRIP_DURATION_DAYS,
    PRODUCT_TYPE,
    REGIONS
} = SgSptConstants;
const {
    ESSENTIAL,
    STANDARD
} = COVER_LEVELS;

const getSelectedTripIndex = (modelVM) => modelVM.value.lobData.privateTravel.coverables.sptTrips.findIndex((el) => el.isSelected);
const getSelectedTripProductType = (modelVM) => modelVM.value.lobData.privateTravel.coverables.sptTrips[getSelectedTripIndex(modelVM)].productType;
const isProductTypeDefined = (modelVM) => !isNil(getSelectedTripProductType(modelVM));

const hasSelectedTripSpecificProductType = (modelVM, productType) => {
    const selectedTripProductType = getSelectedTripProductType(modelVM);
    if (!isNil(selectedTripProductType)) {
        return selectedTripProductType === productType;
    }
    return false;
};

const isSelectedTripAnnual = (modelVM) => hasSelectedTripSpecificProductType(modelVM, PRODUCT_TYPE.ANNUAL);
const isSelectedTripSingle = (modelVM) => hasSelectedTripSpecificProductType(modelVM, PRODUCT_TYPE.SINGLE_TRIP);

const isCoverLevelSelected = (modelVM) => {
    return !!modelVM.lobData.privateTravel.hasSelectedCoverLevel.value;
};

const getSelectedAnnualRegion = (modelVM) => {
    const index = getSelectedTripIndex(modelVM);
    return modelVM.lobData.privateTravel.coverables.sptTrips.value[index].region;
};

const getSelectedTrip = (modelVM) => {
    return modelVM.value.lobData.privateTravel.coverables.sptTrips.find((trip) => trip.isSelected);
};

const isRegionSelected = (modelVM) => {
    return getSelectedAnnualRegion(modelVM) !== undefined;
};

const isRegionSelectedEurope = (modelVM) => {
    return getSelectedAnnualRegion(modelVM) === REGIONS.EUROPE;
};

const getSingleTripDurationInDays = (modelVM) => {
    const tripIndex = getSelectedTripIndex(modelVM);
    const tripStartDate = modelVM.lobData.privateTravel.coverables.sptTrips.value[tripIndex].departureDate;
    const tripEndDate = modelVM.lobData.privateTravel.coverables.sptTrips.value[tripIndex].returnDate;
    return moment(SgDateUtils.toMomentDate(tripEndDate)).diff(moment(SgDateUtils.toMomentDate(tripStartDate)), 'days');
};

const isEssentialCoverLevelAvailable = (modelVM) => {
    const trip = getSelectedTrip(modelVM);
    return getSingleTripDurationInDays(modelVM) <= MAX_ESSENTIAL_SINGLE_TRIP_DURATION_DAYS
        && trip.isEssentialCovelLevelAvailable;
};

const isSpecifiedTripTypeAvailable = (modelVM, productType) => {
    return _.some(modelVM.lobData.privateTravel.coverables.sptTrips.value, (trip) => {
        return trip.productType === productType;
    });
};

const getNumberOfSingleTrips = (modelVM) => {
    return modelVM.lobData.privateTravel.coverables.sptTrips.value.filter((trip) => trip.productType === PRODUCT_TYPE.SINGLE_TRIP).length;
};

const getSpecifiedTrip = (modelVM, productType) => {
    return modelVM.lobData.privateTravel.coverables.sptTrips.value.find((trip) => trip.productType === productType);
};

const getAnnualTrip = (modelVM) => {
    return getSpecifiedTrip(modelVM, PRODUCT_TYPE.ANNUAL);
};

const getSingleTrip = (modelVM) => {
    return getSpecifiedTrip(modelVM, PRODUCT_TYPE.SINGLE_TRIP);
};

const setUnselectedSingleTripDates = (modelVM) => {
    const singleTrip = getSingleTrip(modelVM);
    singleTrip.departureDate = modelVM.baseData.periodStartDate.value;
    const returnDate = SgDateUtil.toMomentDate(singleTrip.departureDate).add(1, 'days').toDate();
    singleTrip.returnDate = SgDateUtil.toLocalDateDto(returnDate);
};

const resetSelectedCoverLevel = (modelVM) => {
    let coverLevel = '';
    if (isSelectedTripAnnual(modelVM)) {
        coverLevel = STANDARD;
    } else {
        coverLevel = isEssentialCoverLevelAvailable(modelVM) ? ESSENTIAL : STANDARD;
    }
    _.set(getSelectedTrip(modelVM), 'coverLevel', coverLevel);
    _.set(modelVM, 'lobData.privateTravel.hasSelectedCoverLevel', undefined);
};

const handleSingleTripEssentialExclusion = (modelVM) => {
    if (isSelectedTripSingle(modelVM)
        && isCoverLevelSelected(modelVM)
        && !isEssentialCoverLevelAvailable(modelVM)
        && getSelectedTrip(modelVM).coverLevel === ESSENTIAL) {
        resetSelectedCoverLevel(modelVM);
    }
};

const removeUnselectedTrip = (modelVM) => {
    const trips = modelVM.lobData.privateTravel.coverables.sptTrips.value;
    if (modelVM.lobData.privateTravel.coverables.sptTrips.value.length === 2) {
        const unselectedTrip = trips.find((trip) => !trip.isSelected);
        trips.splice(trips.indexOf(unselectedTrip), 1);
    }
};

const handleUnselectedSingleTrip = (modelVM) => {
    if (modelVM.lobData.privateTravel.coverables.sptTrips.value.length === 2) {
        if (moment(modelVM.lobData.privateTravel.preUpgradeDepartureDate.value).isBefore(moment(), 'day')) {
            removeUnselectedTrip(modelVM);
            _.set(modelVM, 'lobData.privateTravel.preUpgradeDepartureDate.value', undefined);
            _.set(modelVM, 'lobData.privateTravel.preUpgradeReturnDate.value', undefined);
            return;
        }
        setUnselectedSingleTripDates(modelVM);
    }
};

const handleTripsOnQuotedSubmission = (modelVM) => {
    if (!modelVM.quoteID.value) {
        return;
    }

    if (modelVM.lobData.privateTravel.coverables.sptTrips.value.length < 2) {
        if (getSelectedTripProductType(modelVM) !== SgSessionStorageService.getItem(SgSessionStorageService.KEY.SPT_QUOTED_PRODUCT_TYPE)) {
            resetSelectedCoverLevel(modelVM);
        }
        return;
    }
    const trips = modelVM.lobData.privateTravel.coverables.sptTrips.value;
    const selectedTrip = getSelectedTrip(modelVM);
    const unselectedTrip = trips.find((trip) => !trip.isSelected);

    if (selectedTrip.productType === unselectedTrip.productType) {
        removeUnselectedTrip(modelVM);
        resetSelectedCoverLevel(modelVM);
        return;
    }

    if (isSelectedTripAnnual(modelVM)) {
        handleUnselectedSingleTrip(modelVM);
    } else {
        handleSingleTripEssentialExclusion(modelVM);
    }
};

const getNumberOfTravellers = (modelVM) => modelVM.lobData.privateTravel.coverables.sptTravellers.length;

const isEssentialSuitableBasedOnTripCostOrExcess = (modelVM, isConflictModalShown, canStandardOrPlusBeSelected, prevSelectedCoverLevel) => {
    const selectedTripVM = getSelectedTrip(modelVM);
    const numberOfTravellers = getNumberOfTravellers(modelVM);
    let perTripValue = selectedTripVM.cancelTripValue
        ? selectedTripVM.cancelTripValue.amount * numberOfTravellers : 0;

    if (perTripValue === 0 && selectedTripVM.tripValue_Ext) {
        perTripValue = selectedTripVM.tripValue_Ext * numberOfTravellers;
    }
    if (isCoverLevelSelected(modelVM) && selectedTripVM.coverLevel === ESSENTIAL) {
        return true;
    }
    if (isConflictModalShown && prevSelectedCoverLevel === ESSENTIAL) {
        return true;
    }
    const isEssentialAvailableAndValuesAreValid = isEssentialCoverLevelAvailable(modelVM)
        && !(perTripValue > SINGLE_TRIP_CANCELLATION_LIMITS[ESSENTIAL]
            || selectedTripVM.excessValueExt !== EXCESS_CODES.EXCESS_150);
    return canStandardOrPlusBeSelected
        ? isEssentialAvailableAndValuesAreValid
        : isEssentialAvailableAndValuesAreValid && !isCoverLevelSelected(modelVM);
};

const downgradeToSingleTrip = (modelVM) => {
    const modelCopy = _.clone(modelVM);
    const singleTrip = getSingleTrip(modelCopy);
    const lobData = modelCopy.lobData.privateTravel.value;
    const wasCoverLevelSelected = SgSessionStorageService.getItem(SgSessionStorageService.KEY.SPT_WAS_COVER_LEVEL_SELECTED) === 'true';
    singleTrip.isSelected = true;
    getAnnualTrip(modelCopy).isSelected = false;
    singleTrip.departureDate = lobData.preUpgradeDepartureDate;
    singleTrip.returnDate = lobData.preUpgradeReturnDate;
    singleTrip.coverlevel = lobData.preUpgradeCoverLevel;
    lobData.preUpgradeDepartureDate = undefined;
    lobData.preUpgradeReturnDate = undefined;
    lobData.preUpgradeCoverLevel = undefined;
    modelCopy.baseData.periodStartDate.value = SgDateUtil.toLocalDateDto(Date.now());
    lobData.hasSelectedCoverLevel = wasCoverLevelSelected ? true : undefined;
    return modelCopy;
};

const shouldShowSingleTripOffer = (modelVM) => {
    return isSelectedTripAnnual(modelVM) && modelVM.lobData.privateTravel.shouldOfferSingleTrip.value;
};

export default {
    getSelectedTripIndex,
    isProductTypeDefined,
    downgradeToSingleTrip,
    isSelectedTripAnnual,
    isSelectedTripSingle,
    getSelectedTripProductType,
    isCoverLevelSelected,
    getSelectedAnnualRegion,
    isRegionSelected,
    getSingleTripDurationInDays,
    isRegionSelectedEurope,
    isEssentialCoverLevelAvailable,
    isSpecifiedTripTypeAvailable,
    getNumberOfSingleTrips,
    getAnnualTrip,
    getSingleTrip,
    handleTripsOnQuotedSubmission,
    setUnselectedSingleTripDates,
    getSelectedTrip,
    shouldShowSingleTripOffer,
    removeUnselectedTrip,
    resetSelectedCoverLevel,
    handleUnselectedSingleTrip,
    isEssentialSuitableBasedOnTripCostOrExcess
};
